import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnDestroy,
  OnInit,
  ElementRef,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  inject,
  signal,
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { PiensoApp } from "./interfaces";
import { NAV_CONFIG, NavItem, NavChildItem } from "./topbar.config";
import { firstValueFrom, Subject } from "rxjs";
import { NavigationConfigService } from "./navigation-config.service";
import { Overlay, OverlayRef, ConnectedPosition } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { UserProfileService } from "@shared/auth/user-profile.service";
import { injectHasRoleAccess } from "@shared/auth/role.guard";
import { Logo, Menu, type IMenuItem, ButtonIcon, Navigation } from "@ui";

@Component({
  selector: "pienso-topbar",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["./topbar.component.scss"],
  imports: [Logo, Navigation, Menu, ButtonIcon, RouterLink],
  template: `
    <div class="topbar">
      <a class="topbar__logo" routerLink="/core/home" aria-label="Home">
        <p-logo />
      </a>

      <p-navigation class="topbar__nav" [navItems]="filteredNav()" />

      <div class="topbar__account">
        <p-button-icon-ghosted-lg
          #accountBtn
          class="topbar__account-btn"
          icon="user"
          iconSize="md"
          (click)="toggleAccountMenu()" />
      </div>
    </div>

    <ng-template #accountMenuTemplate>
      <p-menu [menuItems]="accountMenuItems()" (itemSelected)="onAccountMenuSelected($event)" />
    </ng-template>
  `,
})
export class TopbarComponent implements OnInit, OnDestroy {
  readonly #userProfile = inject(UserProfileService);
  readonly #hasRoleAccessFn = injectHasRoleAccess();
  readonly #configService = inject(NavigationConfigService);
  readonly #overlay = inject(Overlay);
  readonly #viewContainerRef = inject(ViewContainerRef);

  readonly accountBtnRef = viewChild.required("accountBtn", { read: ElementRef<HTMLElement> });
  readonly accountMenuTemplate = viewChild.required<TemplateRef<unknown>>("accountMenuTemplate");

  readonly #staticMenuItems: IMenuItem[] = [
    {
      label: "User manual",
      icon: "book",
      action: () => window.open("https://pienso.atlassian.net/wiki/spaces/usermanual/overview", "_blank"),
    },
    {
      label: "Support",
      icon: "circle-questionmark",
      action: () => window.open("https://pienso.atlassian.net/servicedesk/customer/portals", "_blank"),
    },
    {
      label: "Log out",
      icon: "log-out",
      action: () => this.#logout(),
    },
  ];

  filteredNav = signal<NavItem[]>([]);

  readonly accountMenuItems = computed<IMenuItem[]>(() => {
    const email = this.#userProfile.email();
    return [...(email ? [{ label: email, disabled: true }] : []), ...this.#staticMenuItems];
  });

  #accountOverlayRef: OverlayRef | null = null;
  readonly #destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    const piensoApps = await firstValueFrom(this.#configService.getPiensoApps());
    const filtered = this.#filterNavByApps(NAV_CONFIG, piensoApps);
    this.filteredNav.set(this.#applyRoleAccess(filtered));
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#closeAccountMenu();
  }

  toggleAccountMenu(): void {
    if (this.#accountOverlayRef) {
      this.#closeAccountMenu();
      return;
    }

    const positions: ConnectedPosition[] = [{ originX: "end", originY: "bottom", overlayX: "end", overlayY: "top" }];

    const positionStrategy = this.#overlay.position().flexibleConnectedTo(this.accountBtnRef()).withPositions(positions);

    this.#accountOverlayRef = this.#overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: "cdk-overlay-transparent-backdrop",
      scrollStrategy: this.#overlay.scrollStrategies.reposition(),
    });

    this.#accountOverlayRef.backdropClick().subscribe(() => this.#closeAccountMenu());

    const portal = new TemplatePortal(this.accountMenuTemplate(), this.#viewContainerRef);
    this.#accountOverlayRef.attach(portal);
  }

  onAccountMenuSelected(item: IMenuItem): void {
    if (item.action) {
      item.action();
    }
    this.#closeAccountMenu();
  }

  #closeAccountMenu(): void {
    if (this.#accountOverlayRef) {
      this.#accountOverlayRef.dispose();
      this.#accountOverlayRef = null;
    }
  }

  #logout(): void {
    let url = window.location.origin;
    url += "/auth/realms/Pienso/protocol/openid-connect/logout?post_logout_redirect_uri=";
    url += encodeURIComponent(window.location.origin);
    url += "&client_id=angular-web";
    window.location.assign(url);
  }

  #filterNavByApps(items: NavItem[], enabledApps: PiensoApp[]): NavItem[] {
    return items
      .map((item) => {
        if (!item.children) return item;
        const filteredChildren = item.children.filter(
          (child) => !child.requiredApp || enabledApps.includes(child.requiredApp),
        );
        return { ...item, children: filteredChildren };
      })
      .filter((item) => !item.requiredApp || enabledApps.includes(item.requiredApp))
      .filter((item) => {
        if (!item.children || item.children.length === 0) return true;
        return item.children.some((c) => c.visible !== false);
      });
  }

  #applyRoleAccess(items: NavItem[]): NavItem[] {
    return items.map((item) => ({
      ...item,
      disabled: item.role ? !this.#hasRoleAccess(item) : false,
      children: item.children ? this.#applyChildRoleAccess(item.children) : undefined,
    }));
  }

  #applyChildRoleAccess(items: NavChildItem[]): NavChildItem[] {
    return items.map((item) => ({
      ...item,
      disabled: item.role ? !this.#hasRoleAccess(item) : false,
    }));
  }

  #hasRoleAccess(item: { role?: string }): boolean {
    if (!item.role) return true;
    return this.#hasRoleAccessFn(item.role);
  }
}
